import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { SkeletonRows } from './Feedback'
import FirstPrinciplesGuide from './FirstPrinciplesGuide'

// =======================================================================
// First-Principles Workbench — a professional estimating panel embedded
// directly in the Rate Build-Up workflow (not a generic modal form).
//
// STRICT RULE ENFORCED THROUGHOUT: every derived number in Section 3
// (Crew Cost/Shift, Adjusted Output, Direct Unit Rate, Line Cost) is
// fetched fresh from first_principles_rate_breakdown / crew cost RPCs
// after every edit. Nothing here re-implements the costing formulas —
// React only renders what the backend already computed.
// =======================================================================

const COST_BASIS_OPTIONS = [
  { value: 'time_based', label: 'Time-based (hourly/daily/shift)' },
  { value: 'production_based', label: 'Production-based (per output unit)' },
  { value: 'equipment_hourly', label: 'Equipment (operating hours)' },
]

const TIME_UNITS = ['hour', 'day', 'shift', 'week']

export default function FirstPrinciplesWorkbench({ tenderId, boqItem, revisionId, pricingDate, existingLine, onSaved, onCancel, onError }) {
  const [crews, setCrews] = useState(null)
  const [calendars, setCalendars] = useState([])
  const [resources, setResources] = useState([])
  const [productivityItems, setProductivityItems] = useState([])
  const [allFactors, setAllFactors] = useState([])

  const [selectedCrewId, setSelectedCrewId] = useState(existingLine?.crew_id || '')
  const [selectedProductivityId, setSelectedProductivityId] = useState(existingLine?.productivity_library_id || '')
  const [selectedFactorIds, setSelectedFactorIds] = useState(existingLine?.factor_ids || [])
  const [assumptions, setAssumptions] = useState(existingLine?.assumptions || '')

  const [crewMembers, setCrewMembers] = useState(null)
  const [breakdown, setBreakdown] = useState(null)
  const [breakdownLoading, setBreakdownLoading] = useState(false)
  const [missingRateDetails, setMissingRateDetails] = useState({})

  const [showNewCrew, setShowNewCrew] = useState(false)
  const [showNewProductivity, setShowNewProductivity] = useState(false)
  const [saving, setSaving] = useState(false)

  // -----------------------------------------------------------------
  // Load reference data (crews, calendars, resources, productivity)
  // -----------------------------------------------------------------
  const loadReferenceData = useCallback(async () => {
    const [crewRes, calRes, resRes, prodRes, factorRes] = await Promise.all([
      supabase.from('crew').select('id, name, project_calendar_id, project_calendar(name)').eq('tender_id', tenderId).order('name'),
      supabase.from('project_calendar').select('id, name, hours_per_shift, shifts_per_day, working_days_per_week').eq('tender_id', tenderId),
      // Tender-scoped: same tenderId already used for crew/calendar two
      // lines above.
      supabase.from('resource_library').select('id, code, description, unit, resource_type').eq('is_active', true).eq('tender_id', tenderId).order('code'),
      supabase.from('productivity_library').select('*').eq('is_active', true).order('activity_description'),
      supabase.from('productivity_factor').select('*'),
    ])
    if (crewRes.error) return onError(crewRes.error.message)
    setCrews(crewRes.data)
    setCalendars(calRes.data || [])
    setResources(resRes.data || [])
    setProductivityItems(prodRes.data || [])
    setAllFactors(factorRes.data || [])
  }, [tenderId, onError])

  useEffect(() => { loadReferenceData() }, [loadReferenceData])

  // -----------------------------------------------------------------
  // Load crew members for the selected crew, with their resource +
  // resource_rate resolved for THIS revision's pricing_date — purely
  // for display; the authoritative cost still comes from the backend
  // function below.
  // -----------------------------------------------------------------
  const loadCrewMembers = useCallback(async () => {
    if (!selectedCrewId) { setCrewMembers(null); return }
    const { data, error } = await supabase
      .from('crew_member')
      .select(`
        id, quantity, resource_costing_profile_id,
        resource_costing_profile (
          id, cost_basis, time_unit, production_rate_per_shift, operating_hours_per_shift, utilization_pct,
          resource_library_id, resource_library ( code, description, unit )
        )
      `)
      .eq('crew_id', selectedCrewId)
    if (error) return onError(error.message)

    // Resolve each member's applicable rate for the revision's pricing_date.
    const withRates = await Promise.all((data || []).map(async (m) => {
      const resourceLibraryId = m.resource_costing_profile?.resource_library_id
      if (!resourceLibraryId) return { ...m, resolvedRate: null }
      const { data: rateRows } = await supabase
        .from('resource_rate')
        .select('rate, currency, effective_from, effective_to')
        .eq('resource_library_id', resourceLibraryId)
        .lte('effective_from', pricingDate)
        .order('effective_from', { ascending: false })
        .limit(5)
      const applicable = (rateRows || []).find((r) => !r.effective_to || r.effective_to > pricingDate)
      return { ...m, resolvedRate: applicable || null }
    }))
    setCrewMembers(withRates)
  }, [selectedCrewId, pricingDate, onError])

  useEffect(() => { loadCrewMembers() }, [loadCrewMembers])

  // -----------------------------------------------------------------
  // Fetch the live calculation breakdown from the backend. This is the
  // ONLY place numbers for Section 3 come from. Uses the RPC directly
  // for crew cost (works even before a first_principles_detail row
  // exists yet), and first_principles_rate_breakdown once the line is
  // saved (existingLine case).
  // -----------------------------------------------------------------
  const refreshBreakdown = useCallback(async () => {
    if (!selectedCrewId) { setBreakdown(null); return }
    setBreakdownLoading(true)

    const { data: crewCostRows, error: crewCostErr } = await supabase.rpc('resolve_crew_cost_per_shift_v2', {
      p_crew_id: selectedCrewId, p_pricing_date: pricingDate,
    })
    if (crewCostErr) { onError(crewCostErr.message); setBreakdownLoading(false); return }
    const crewCost = crewCostRows?.[0]

    let productivity = null
    if (selectedProductivityId) {
      const { data: pl } = await supabase.from('productivity_library').select('*').eq('id', selectedProductivityId).single()
      productivity = pl
    }

    const combinedFactorPct = selectedFactorIds.reduce((sum, fid) => {
      const f = allFactors.find((af) => af.id === fid)
      return sum + (f ? Number(f.adjustment_pct) : 0)
    }, 0)

    // Mirror the backend's exact formula for a LIVE PREVIEW only — this
    // pre-save preview is unavoidable since first_principles_detail
    // doesn't exist yet to query the View. Once saved, the real
    // first_principles_rate_breakdown value (fetched in RateBuildUpPage)
    // is what is actually persisted and displayed as the line's rate —
    // this preview never writes anywhere and is clearly labeled PREVIEW.
    let adjustedOutput = null
    if (productivity && crewCost && !crewCost.has_missing_rate) {
      if (productivity.basis === 'crew_output_per_shift') {
        adjustedOutput = productivity.crew_output_per_shift * (1 + combinedFactorPct / 100) * ((productivity.equipment_utilization_pct ?? 100) / 100)
      } else if (productivity.basis === 'man_hours_per_unit') {
        const denom = productivity.man_hours_per_unit * (1 - combinedFactorPct / 100)
        adjustedOutput = denom !== 0 ? crewCost.total_man_hours_per_shift / denom : null
      }
    }
    const directUnitRate = adjustedOutput && crewCost && !crewCost.has_missing_rate ? crewCost.total_cost_per_shift / adjustedOutput : null

    setBreakdown({
      crewCost,
      productivity,
      combinedFactorPct,
      adjustedOutput,
      directUnitRate,
      isPreview: true,
    })
    setBreakdownLoading(false)

    // If any crew member's rate is missing, fetch WHY it's missing —
    // purely explanatory: does a rate exist at all for this resource,
    // and if so, when does it actually start? This does not change
    // which rate is selected for calculation (still resolved entirely
    // by resolve_crew_cost_per_shift_v2 above) — it only explains the
    // gap to the estimator.
    if (crewCost?.has_missing_rate && crewCost.missing_rate_resource_codes?.length > 0) {
      await loadMissingRateDetails(crewCost.missing_rate_resource_codes)
    } else {
      setMissingRateDetails({})
    }
  }, [selectedCrewId, selectedProductivityId, selectedFactorIds, allFactors, pricingDate, onError])

  const [tenderCurrency, setTenderCurrency] = useState(null)
  useEffect(() => {
    if (!tenderId) return
    supabase.from('tender').select('currency').eq('id', tenderId).maybeSingle()
      .then(({ data }) => setTenderCurrency(data?.currency ?? null))
  }, [tenderId])

  const loadMissingRateDetails = useCallback(async (resourceCodes) => {
    // Tender-scoped: code alone is no longer a safe match now that
    // Resource Dictionary is Tender-owned (two tenders may each have
    // their own resource using the same code).
    const { data: resourceRows, error: resErr } = await supabase
      .from('resource_library')
      .select('id, code, description')
      .in('code', resourceCodes)
      .eq('tender_id', tenderId)
    if (resErr) return

    const details = {}
    await Promise.all((resourceRows || []).map(async (res) => {
      // All rates ever entered for this resource, earliest first — used
      // only to explain the gap (e.g. "earliest available rate starts
      // on X"), never to select a rate for calculation.
      const { data: allRates } = await supabase
        .from('resource_rate')
        .select('rate, currency, effective_from, effective_to')
        .eq('resource_library_id', res.id)
        .order('effective_from', { ascending: true })

      if (!allRates || allRates.length === 0) {
        details[res.code] = { description: res.description, status: 'no_rate_configured' }
        return
      }

      // A rate exists for the pricing date, but its currency differs
      // from the tender's pricing currency and no exchange_rate row
      // covers that pair as of the pricing date. This is distinct from
      // "no rate configured" -- the rate exists, it's the FX link that's
      // missing. Checked only when a same/earlier-dated rate exists.
      const effectiveRate = [...allRates].reverse().find((r) => r.effective_from <= pricingDate)
      if (effectiveRate && effectiveRate.currency !== tenderCurrency) {
        const { data: fxRows } = await supabase
          .from('exchange_rate')
          .select('rate, effective_date')
          .eq('from_currency', effectiveRate.currency)
          .eq('to_currency', tenderCurrency)
          .lte('effective_date', pricingDate)
        if (!fxRows || fxRows.length === 0) {
          details[res.code] = {
            description: res.description,
            status: 'fx_rate_missing',
            resourceCurrency: effectiveRate.currency,
            tenderCurrency,
            rate: effectiveRate.rate,
          }
          return
        }
      }

      // The earliest-starting rate that begins AFTER the pricing date —
      // this is purely informational (what's the soonest a valid rate
      // becomes available), not the rate used in any calculation.
      const nextAvailable = allRates.find((r) => r.effective_from > pricingDate)
      details[res.code] = {
        description: res.description,
        status: 'rate_starts_later',
        rate: nextAvailable?.rate ?? allRates[allRates.length - 1].rate,
        currency: nextAvailable?.currency ?? allRates[allRates.length - 1].currency,
        availableFrom: nextAvailable?.effective_from ?? allRates[allRates.length - 1].effective_from,
      }
    }))
    setMissingRateDetails(details)
  }, [pricingDate])

  useEffect(() => { refreshBreakdown() }, [refreshBreakdown])

  // -----------------------------------------------------------------
  // If editing an existing first_principles line, fetch the REAL
  // persisted breakdown from the backend View (authoritative, not the
  // client preview above).
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!existingLine?.rate_buildup_line_id) return
    supabase
      .from('first_principles_rate_breakdown')
      .select('*')
      .eq('rate_buildup_line_id', existingLine.rate_buildup_line_id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setBreakdown({
            crewCost: { total_cost_per_shift: data.total_cost_per_shift, total_man_hours_per_shift: data.total_man_hours_per_shift, has_missing_rate: data.has_missing_rate, missing_rate_resource_codes: data.missing_rate_resource_codes },
            productivity: { basis: data.basis, activity_description: data.activity_description, unit: data.productivity_unit, crew_output_per_shift: data.base_output_per_shift, man_hours_per_unit: data.base_man_hours_per_unit },
            combinedFactorPct: data.combined_adjustment_pct,
            adjustedOutput: data.adjusted_output_per_shift,
            directUnitRate: data.direct_unit_rate,
            isPreview: false,
          })
        }
      })
  }, [existingLine])

  async function handleCreateCrew(name, calendarId) {
    const { data, error } = await supabase.from('crew').insert({ tender_id: tenderId, name, project_calendar_id: calendarId }).select().single()
    if (error) { onError(error.message); return }
    await loadReferenceData()
    setSelectedCrewId(data.id)
    setShowNewCrew(false)
  }

  async function handleAddMember(resourceLibraryId, costBasis, quantity, timeUnit, productionRate, operatingHours, utilizationPct) {
    const { data: profile, error: profileErr } = await supabase
      .from('resource_costing_profile')
      .insert({
        resource_library_id: resourceLibraryId,
        cost_basis: costBasis,
        time_unit: costBasis === 'time_based' ? timeUnit : null,
        production_rate_per_shift: costBasis === 'production_based' ? productionRate : null,
        operating_hours_per_shift: costBasis === 'equipment_hourly' ? operatingHours : null,
        utilization_pct: costBasis === 'equipment_hourly' ? (utilizationPct || null) : null,
      })
      .select().single()
    if (profileErr) { onError(profileErr.message); return }

    const { error: memberErr } = await supabase.from('crew_member').insert({ crew_id: selectedCrewId, resource_costing_profile_id: profile.id, quantity })
    if (memberErr) { onError(memberErr.message); return }
    await loadCrewMembers()
    await refreshBreakdown()
  }

  async function handleRemoveMember(memberId) {
    const { error } = await supabase.from('crew_member').delete().eq('id', memberId)
    if (error) { onError(error.message); return }
    await loadCrewMembers()
    await refreshBreakdown()
  }

  async function handleCreateProductivity(payload) {
    const { data, error } = await supabase.from('productivity_library').insert(payload).select().single()
    if (error) { onError(error.message); return }
    await loadReferenceData()
    setSelectedProductivityId(data.id)
    setShowNewProductivity(false)
  }

  function toggleFactor(factorId) {
    setSelectedFactorIds((prev) => (prev.includes(factorId) ? prev.filter((f) => f !== factorId) : [...prev, factorId]))
  }

  // -----------------------------------------------------------------
  // Save: persist first_principles_detail (+ factor links), then the
  // rate_buildup_line. All financial values remain backend-derived —
  // this write only records the estimator's configuration choices.
  // -----------------------------------------------------------------
  async function handleSave() {
    if (!selectedCrewId || !selectedProductivityId) {
      onError('Select both a Crew and a Productivity standard before saving.')
      return
    }
    setSaving(true)
    onError(null)

    let detailId = existingLine?.first_principles_detail_id
    if (detailId) {
      const { error } = await supabase.from('first_principles_detail').update({ productivity_library_id: selectedProductivityId, assumptions: assumptions || null }).eq('id', detailId)
      if (error) { onError(error.message); setSaving(false); return }
      await supabase.from('first_principles_detail_factor').delete().eq('first_principles_detail_id', detailId)
    } else {
      const { data, error } = await supabase.from('first_principles_detail').insert({ crew_id: selectedCrewId, productivity_library_id: selectedProductivityId, assumptions: assumptions || null }).select().single()
      if (error) { onError(error.message); setSaving(false); return }
      detailId = data.id
    }

    if (selectedFactorIds.length > 0) {
      const factorRows = selectedFactorIds.map((fid) => {
        const f = allFactors.find((af) => af.id === fid)
        return { first_principles_detail_id: detailId, productivity_factor_id: fid, applied_adjustment_pct: f.adjustment_pct }
      })
      const { error: factorErr } = await supabase.from('first_principles_detail_factor').insert(factorRows)
      if (factorErr) { onError(factorErr.message); setSaving(false); return }
    }

    if (existingLine?.rate_buildup_line_id) {
      // detail already linked; nothing else to update on the line itself
    } else {
      const { error: lineErr } = await supabase.from('rate_buildup_line').insert({ boq_item_id: boqItem.id, method: 'first_principles', first_principles_detail_id: detailId })
      if (lineErr) { onError(lineErr.message); setSaving(false); return }
    }

    setSaving(false)
    onSaved('First-Principles rate saved.')
  }

  const selectedProductivity = productivityItems.find((p) => p.id === selectedProductivityId)
  // Simple, literal string comparison — no unit conversion, inference, or
  // normalization performed. A mismatch here is informational only; the
  // estimator can proceed if there is a legitimate special case (per
  // approved requirement: non-blocking).
  const unitMismatch = selectedProductivity && boqItem?.unit && selectedProductivity.unit &&
    selectedProductivity.unit.trim().toLowerCase() !== boqItem.unit.trim().toLowerCase()

  const hasMissingRate = breakdown?.crewCost?.has_missing_rate
  const canSave = selectedCrewId && selectedProductivityId && crewMembers?.length > 0 && !hasMissingRate

  return (
    <div style={{ border: `2px solid ${colors.primary}`, borderRadius: 10, background: colors.surface, marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px', background: colors.primary, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.3 }}>FIRST-PRINCIPLES RATE BUILD-UP — {boqItem.item_no} {boqItem.description}</span>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13 }}>✕ Close</button>
      </div>

      <div style={{ padding: '0 16px' }}>
        <FirstPrinciplesGuide />
      </div>

      <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* ============================= SECTION 1: CREW ============================= */}
        <SectionCard title="1 · CREW">
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <Select value={selectedCrewId} onChange={(e) => setSelectedCrewId(e.target.value)} style={{ flex: 1, fontSize: 13 }}>
              <option value="">Select existing crew…</option>
              {crews?.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.project_calendar?.name})</option>)}
            </Select>
            <Button variant="secondary" size="sm" onClick={() => setShowNewCrew((v) => !v)}>+ New Crew</Button>
          </div>

          {showNewCrew && <NewCrewInline calendars={calendars} onCreate={handleCreateCrew} onCancel={() => setShowNewCrew(false)} />}

          {selectedCrewId && (
            <>
              <CrewMembersTable
                members={crewMembers}
                resources={resources}
                onRemove={handleRemoveMember}
                tenderCurrency={tenderCurrency}
              />
              <AddMemberInline resources={resources} onAdd={handleAddMember} />
              {hasMissingRate && (
                <RateMissingExplanation missingRateDetails={missingRateDetails} pricingDate={pricingDate} />
              )}
              {crewMembers?.length === 0 && <WarningBox>⚠ This crew has no members yet — add at least one.</WarningBox>}
            </>
          )}
        </SectionCard>

        {/* ========================= SECTION 2: PRODUCTIVITY ========================= */}
        <SectionCard title="2 · PRODUCTIVITY">
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <Select value={selectedProductivityId} onChange={(e) => setSelectedProductivityId(e.target.value)} style={{ flex: 1, fontSize: 13 }}>
              <option value="">Select productivity standard…</option>
              {productivityItems.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.activity_description} — {p.basis === 'man_hours_per_unit' ? `${formatNumber(p.man_hours_per_unit)} hr/${p.unit}` : `${formatNumber(p.crew_output_per_shift)} ${p.unit}/shift`}
                </option>
              ))}
            </Select>
            <Button variant="secondary" size="sm" onClick={() => setShowNewProductivity((v) => !v)}>+ New</Button>
          </div>

          {showNewProductivity && (
            <NewProductivityInline crews={crews} onCreate={handleCreateProductivity} onCancel={() => setShowNewProductivity(false)} />
          )}

          {unitMismatch && (
            <UnitMismatchWarning boqUnit={boqItem.unit} productivityUnit={selectedProductivity.unit} />
          )}

          {selectedProductivityId && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 6 }}>Applicable Factors</div>
              {allFactors.filter((f) => f.productivity_library_id === selectedProductivityId).length === 0 && (
                <div style={{ fontSize: 12, color: colors.textMuted }}>No factors defined for this standard.</div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {allFactors.filter((f) => f.productivity_library_id === selectedProductivityId).map((f) => (
                  <button
                    key={f.id}
                    onClick={() => toggleFactor(f.id)}
                    style={{
                      padding: '5px 10px', borderRadius: 14, fontSize: 12, cursor: 'pointer',
                      border: selectedFactorIds.includes(f.id) ? `1.5px solid ${colors.primary}` : `1px solid ${colors.border}`,
                      background: selectedFactorIds.includes(f.id) ? '#EEF2FF' : '#fff',
                      color: selectedFactorIds.includes(f.id) ? colors.primary : colors.textSecondary,
                      fontWeight: selectedFactorIds.includes(f.id) ? 700 : 500,
                    }}
                  >
                    {f.factor_name} ({f.adjustment_pct > 0 ? '+' : ''}{f.adjustment_pct}%)
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Assumptions (optional)</label>
            <Input value={assumptions} onChange={(e) => setAssumptions(e.target.value)} style={{ width: '100%', marginTop: 4, fontSize: 13 }} placeholder="Notes for audit trail…" />
          </div>
        </SectionCard>
      </div>

      {/* ===================== SECTION 3: CALCULATION BREAKDOWN ===================== */}
      <div style={{ margin: '0 16px 16px', border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '8px 14px', background: '#F5F6F8', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          3 · Calculation Breakdown {breakdown?.isPreview && <span style={{ color: colors.textMuted, fontWeight: 500, textTransform: 'none' }}>(live preview — from backend functions)</span>}
        </div>
        {breakdownLoading ? (
          <div style={{ padding: 14 }}><SkeletonRows rows={1} cols={5} /></div>
        ) : !selectedCrewId ? (
          <div style={{ padding: 14, fontSize: 13, color: colors.textMuted }}>Select a crew to see the calculation.</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'stretch', flexWrap: 'wrap' }}>
            <BreakdownStep label="Crew Cost / Shift" value={hasMissingRate ? 'RATE MISSING' : `${formatNumber(breakdown?.crewCost?.total_cost_per_shift)}${tenderCurrency ? ` ${tenderCurrency}` : ''}`} isWarning={hasMissingRate} />
            <Arrow />
            <BreakdownStep
              label="Productivity"
              value={breakdown?.productivity ? (breakdown.productivity.basis === 'man_hours_per_unit' ? `${formatNumber(breakdown.productivity.man_hours_per_unit)} hr/${breakdown.productivity.unit}` : `${formatNumber(breakdown.productivity.crew_output_per_shift)} ${breakdown.productivity.unit}/shift`) : '—'}
            />
            <Arrow />
            <BreakdownStep label="Applied Factors" value={breakdown?.combinedFactorPct ? `${breakdown.combinedFactorPct > 0 ? '+' : ''}${formatNumber(breakdown.combinedFactorPct)}%` : 'None'} />
            <Arrow />
            <BreakdownStep label="Adjusted Output" value={breakdown?.adjustedOutput ? `${formatNumber(breakdown.adjustedOutput)} /shift` : '—'} />
            <Arrow />
            <BreakdownStep label="Direct Unit Rate" value={hasMissingRate ? '—' : (breakdown?.directUnitRate ? `${formatNumber(breakdown.directUnitRate)}${tenderCurrency ? ` ${tenderCurrency}` : ''}` : '—')} highlight />
            <Arrow />
            <BreakdownStep label="BOQ Quantity" value={`${formatNumber(boqItem.resolved_quantity)} ${boqItem.unit}`} />
            <Arrow />
            <BreakdownStep
              label="Line Cost"
              value={hasMissingRate || !breakdown?.directUnitRate ? '—' : `${formatNumber(boqItem.resolved_quantity * breakdown.directUnitRate)}${tenderCurrency ? ` ${tenderCurrency}` : ''}`}
              highlight
              final
            />
          </div>
        )}
      </div>

      <div style={{ padding: '0 16px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSave} disabled={!canSave || saving}>{saving ? 'Saving…' : 'Save First-Principles Rate'}</Button>
      </div>
    </div>
  )
}

// =======================================================================
// Sub-components
// =======================================================================
function SectionCard({ title, children }) {
  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: colors.primary, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  )
}

function UnitMismatchWarning({ boqUnit, productivityUnit }) {
  return (
    <div style={{
      marginTop: 8, marginBottom: 10, padding: '10px 12px',
      background: '#FFF7ED', border: '1.5px solid #FDBA74', borderRadius: 6,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#9A3412', marginBottom: 4 }}>
        ⚠ Unit mismatch
      </div>
      <div style={{ fontSize: 12, color: '#9A3412', lineHeight: 1.6 }}>
        BOQ unit: <strong>{boqUnit}</strong><br />
        Productivity unit: <strong>{productivityUnit}</strong><br />
        This productivity standard cannot normally be used to derive a valid unit rate for this BOQ item.
      </div>
    </div>
  )
}

function formatDateReadable(isoDate) {
  if (!isoDate) return '—'
  const d = new Date(isoDate + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function RateMissingExplanation({ missingRateDetails, pricingDate }) {
  const entries = Object.entries(missingRateDetails)
  if (entries.length === 0) {
    // Details not loaded yet (or still fetching) — fall back to a
    // minimal message rather than showing nothing.
    return (
      <div style={{
        marginTop: 8, padding: '10px 12px', background: '#FEF2F2', border: '1.5px solid #FCA5A5', borderRadius: 6,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#991B1B' }}>⚠ Rate unavailable — this line cannot be priced until resolved.</div>
      </div>
    )
  }

  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {entries.map(([code, d]) => (
        <div key={code} style={{ padding: '10px 12px', background: '#FEF2F2', border: '1.5px solid #FCA5A5', borderRadius: 6 }}>
          {d.status === 'no_rate_configured' ? (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#991B1B', marginBottom: 4 }}>⚠ No rate configured</div>
              <div style={{ fontSize: 12, color: '#991B1B', lineHeight: 1.6 }}>
                Resource: <strong>{d.description} ({code})</strong><br />
                No rate has been configured for this resource.
              </div>
            </>
          ) : d.status === 'fx_rate_missing' ? (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#991B1B', marginBottom: 4 }}>⚠ Exchange rate unavailable</div>
              <div style={{ fontSize: 12, color: '#991B1B', lineHeight: 1.6 }}>
                Resource: <strong>{d.description} ({code})</strong><br />
                This resource is priced in <strong>{d.resourceCurrency}</strong> but the tender currency is <strong>{d.tenderCurrency}</strong>,
                and no {d.resourceCurrency}→{d.tenderCurrency} exchange rate is available for the pricing date ({formatDateReadable(pricingDate)}).
              </div>
              <div style={{ fontSize: 12, color: '#991B1B', lineHeight: 1.8, marginTop: 4, direction: 'rtl', textAlign: 'right' }}>
                المورد: <strong>{d.description} ({code})</strong><br />
                سعر الصرف غير متوفر — هذا المورد مُسعَّر بعملة <strong>{d.resourceCurrency}</strong> بينما عملة هذا التندر <strong>{d.tenderCurrency}</strong>،
                ولا يوجد سعر صرف من {d.resourceCurrency} إلى {d.tenderCurrency} بتاريخ التسعير ({formatDateReadable(pricingDate)}).
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#991B1B', marginBottom: 4 }}>⚠ Rate unavailable for pricing date</div>
              <div style={{ fontSize: 12, color: '#991B1B', lineHeight: 1.6 }}>
                Resource: <strong>{d.description} ({code})</strong><br />
                Pricing Date: <strong>{formatDateReadable(pricingDate)}</strong><br />
                Available Rate From: <strong>{formatDateReadable(d.availableFrom)}</strong><br />
                Rate: <strong>{formatNumber(d.rate)} {d.currency}</strong>
              </div>
            </>
          )}
          <div style={{ fontSize: 11, color: '#B91C1C', marginTop: 6, fontStyle: 'italic' }}>
            {d.status === 'no_rate_configured'
              ? 'Add a rate for this resource before using it in a crew.'
              : d.status === 'fx_rate_missing'
              ? 'Add an exchange rate, or correct the resource\'s currency. / أضف سعر صرف، أو صحّح عملة المورد.'
              : 'Update the Pricing Date or add a rate effective on/before the Pricing Date.'}
          </div>
        </div>
      ))}
    </div>
  )
}

function WarningBox({ children }) {
  return (
    <div style={{ marginTop: 8, padding: '8px 10px', background: colors.warningBg, color: colors.warningText, borderRadius: 6, fontSize: 12, fontWeight: 500 }}>
      {children}
    </div>
  )
}

function BreakdownStep({ label, value, highlight, final, isWarning }) {
  return (
    <div style={{
      flex: '1 1 120px', padding: '10px 12px', borderRight: `1px solid ${colors.border}`,
      background: final ? '#EEF2FF' : highlight ? '#FAFBFC' : '#fff',
    }}>
      <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{
        fontSize: final ? 16 : 13, fontWeight: final ? 800 : highlight ? 700 : 500,
        color: isWarning ? colors.danger : final || highlight ? colors.primary : colors.textPrimary,
        marginTop: 2,
      }}>
        {value}
      </div>
    </div>
  )
}

function Arrow() {
  return <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px', color: colors.textMuted, fontSize: 14 }}>→</div>
}

function CrewMembersTable({ members, resources, onRemove, tenderCurrency }) {
  if (members === null) return <SkeletonRows rows={2} cols={5} />
  if (members.length === 0) return null
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 8 }}>
      <thead>
        <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
          <Th>Resource</Th><Th>Cost Basis</Th><Th align="right">Qty</Th><Th align="right">Rate</Th><Th>Unit</Th><Th>Eff. Date</Th><Th></Th>
        </tr>
      </thead>
      <tbody>
        {members.map((m) => {
          const profile = m.resource_costing_profile
          const rl = profile?.resource_library
          const basisDetail =
            profile?.cost_basis === 'time_based' ? `hour: ${profile.time_unit}` :
            profile?.cost_basis === 'production_based' ? `${formatNumber(profile.production_rate_per_shift)}/shift` :
            profile?.cost_basis === 'equipment_hourly' ? `${formatNumber(profile.operating_hours_per_shift)} hrs/shift` : '—'
          return (
            <tr key={m.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
              <Td>{rl?.code} — {rl?.description}</Td>
              <Td>
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: colors.primary }}>{profile?.cost_basis?.replace('_', ' ')}</span>
                <div style={{ fontSize: 11, color: colors.textMuted }}>{basisDetail}</div>
              </Td>
              <Td align="right">{formatNumber(m.quantity)}</Td>
              <Td align="right" style={!m.resolvedRate ? { color: colors.danger, fontWeight: 700 } : {}}>
                {m.resolvedRate
                  ? (m.resolvedRate.currency && m.resolvedRate.currency !== tenderCurrency
                      ? `${formatNumber(m.resolvedRate.rate)} ${m.resolvedRate.currency}`
                      : formatNumber(m.resolvedRate.rate))
                  : 'MISSING'}
              </Td>
              <Td muted>{rl?.unit}</Td>
              <Td muted>{m.resolvedRate?.effective_from || '—'}</Td>
              <Td><button onClick={() => onRemove(m.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 11 }}>✕</button></Td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function AddMemberInline({ resources, onAdd }) {
  const [resourceId, setResourceId] = useState('')
  const [costBasis, setCostBasis] = useState('time_based')
  const [quantity, setQuantity] = useState('1')
  const [timeUnit, setTimeUnit] = useState('hour')
  const [productionRate, setProductionRate] = useState('')
  const [operatingHours, setOperatingHours] = useState('')
  const [utilizationPct, setUtilizationPct] = useState('')

  function submit() {
    if (!resourceId || !quantity) return
    onAdd(resourceId, costBasis, Number(quantity), timeUnit, Number(productionRate) || null, Number(operatingHours) || null, Number(utilizationPct) || null)
    setResourceId(''); setQuantity('1'); setProductionRate(''); setOperatingHours(''); setUtilizationPct('')
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'flex-end', padding: '8px 0', borderTop: `1px dashed ${colors.border}` }}>
      <MiniField label="Resource">
        <Select value={resourceId} onChange={(e) => setResourceId(e.target.value)} style={{ width: 170, fontSize: 12 }}>
          <option value="">Select…</option>
          {resources.map((r) => <option key={r.id} value={r.id}>{r.code}</option>)}
        </Select>
      </MiniField>
      <MiniField label="Cost Basis">
        <Select value={costBasis} onChange={(e) => setCostBasis(e.target.value)} style={{ width: 150, fontSize: 12 }}>
          {COST_BASIS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
      </MiniField>
      <MiniField label="Qty"><Input type="number" step="any" min="0" value={quantity} onChange={(e) => setQuantity(e.target.value)} style={{ width: 60, fontSize: 12 }} /></MiniField>

      {costBasis === 'time_based' && (
        <MiniField label="Time Unit">
          <Select value={timeUnit} onChange={(e) => setTimeUnit(e.target.value)} style={{ width: 90, fontSize: 12 }}>
            {TIME_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </Select>
        </MiniField>
      )}
      {costBasis === 'production_based' && (
        <MiniField label="Output/shift"><Input type="number" step="any" min="0" value={productionRate} onChange={(e) => setProductionRate(e.target.value)} style={{ width: 90, fontSize: 12 }} /></MiniField>
      )}
      {costBasis === 'equipment_hourly' && (
        <>
          <MiniField label="Op. Hrs/shift"><Input type="number" step="any" min="0" value={operatingHours} onChange={(e) => setOperatingHours(e.target.value)} style={{ width: 90, fontSize: 12 }} /></MiniField>
          <MiniField label="Util. %"><Input type="number" step="any" min="0" max="100" value={utilizationPct} onChange={(e) => setUtilizationPct(e.target.value)} style={{ width: 70, fontSize: 12 }} /></MiniField>
        </>
      )}
      <Button size="sm" onClick={submit}>+ Add</Button>
    </div>
  )
}

function NewCrewInline({ calendars, onCreate, onCancel }) {
  const [name, setName] = useState('')
  const [calendarId, setCalendarId] = useState('')
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', padding: 10, background: '#FAFBFC', borderRadius: 6, marginBottom: 10 }}>
      <MiniField label="Crew Name"><Input value={name} onChange={(e) => setName(e.target.value)} style={{ width: 150, fontSize: 12 }} /></MiniField>
      <MiniField label="Calendar">
        <Select value={calendarId} onChange={(e) => setCalendarId(e.target.value)} style={{ width: 140, fontSize: 12 }}>
          <option value="">Select…</option>
          {calendars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
      </MiniField>
      <Button size="sm" onClick={() => name && calendarId && onCreate(name, calendarId)}>Create</Button>
      <Button size="sm" variant="secondary" onClick={onCancel}>Cancel</Button>
    </div>
  )
}

function NewProductivityInline({ crews, onCreate, onCancel }) {
  const [desc, setDesc] = useState('')
  const [unit, setUnit] = useState('')
  const [basis, setBasis] = useState('crew_output_per_shift')
  const [value, setValue] = useState('')
  const [refCrewId, setRefCrewId] = useState('')

  function submit() {
    const payload = { activity_description: desc, unit, productivity_domain: 'labour', basis }
    if (basis === 'crew_output_per_shift') { payload.crew_output_per_shift = Number(value); payload.reference_crew_id = refCrewId }
    else { payload.man_hours_per_unit = Number(value) }
    onCreate(payload)
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'flex-end', padding: 10, background: '#FAFBFC', borderRadius: 6, marginBottom: 10 }}>
      <MiniField label="Activity"><Input value={desc} onChange={(e) => setDesc(e.target.value)} style={{ width: 150, fontSize: 12 }} /></MiniField>
      <MiniField label="Unit"><Input value={unit} onChange={(e) => setUnit(e.target.value)} style={{ width: 60, fontSize: 12 }} /></MiniField>
      <MiniField label="Basis">
        <Select value={basis} onChange={(e) => setBasis(e.target.value)} style={{ width: 160, fontSize: 12 }}>
          <option value="crew_output_per_shift">Crew Output/Shift</option>
          <option value="man_hours_per_unit">Man-Hours/Unit</option>
        </Select>
      </MiniField>
      <MiniField label="Value"><Input type="number" step="any" value={value} onChange={(e) => setValue(e.target.value)} style={{ width: 80, fontSize: 12 }} /></MiniField>
      {basis === 'crew_output_per_shift' && (
        <MiniField label="Ref. Crew">
          <Select value={refCrewId} onChange={(e) => setRefCrewId(e.target.value)} style={{ width: 140, fontSize: 12 }}>
            <option value="">Select…</option>
            {crews?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </MiniField>
      )}
      <Button size="sm" onClick={submit}>Create</Button>
      <Button size="sm" variant="secondary" onClick={onCancel}>Cancel</Button>
    </div>
  )
}

function MiniField({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 10, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase' }}>
      {label}
      {children}
    </label>
  )
}
function Th({ children, align = 'left' }) {
  return <th style={{ textAlign: align, padding: '6px 8px', fontSize: 10, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>{children}</th>
}
function Td({ children, align = 'left', muted, style }) {
  return <td style={{ textAlign: align, padding: '7px 8px', color: muted ? colors.textMuted : colors.textPrimary, ...style }}>{children}</td>
}
